import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { THEME } from 'src/config/config'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'

interface SortbyOption {
  id: string
  label: string
}

const options: { [key: string]: SortbyOption[] } = {
  solutions: [
    { id: 'Default', label: 'Default' },
    { id: 'Alphabetically', label: 'Alphabetically' },
    ...(THEME === 'legaltech' ? [{ id: 'Newest', label: 'Newest' }] : []),
    // ...(THEME === 'legaltech' && SHOW_REVIEWS ? [{ id: 'Rating', label: 'By Rating' }] : []),
  ],
  contents: [
    { id: 'Chronologically', label: 'Chronologically' },
    { id: 'Alphabetically', label: 'Alphabetically' },
  ],
  category_contents: [
    { id: 'Category_Chronologically', label: 'Default' },
    { id: 'Alphabetically', label: 'Alphabetically' },
  ],
}

export type SortByType = 'solutions' | 'contents' | 'category_contents'

interface SortbyDropdownProps {
  className?: string
  type: SortByType
  fromPage?: string
  searchKeyword?: string
  /** When true, hides the Keyword sort option (used for Thought Leadership tab on vendor/alsp/consultant pages) */
  hideKeywordSort?: boolean
}

export const SortbyDropdown: React.FC<SortbyDropdownProps> = ({
  className = '',
  type,
  fromPage = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  searchKeyword = '',
  hideKeywordSort = false,
}) => {
  const router = useRouter()
  const decodedSearchKeyword = useDecodedKeyword(router.query.keyword)
  // const sortByOptions = options[type as string]
  const sortByOptions = useMemo(() => {
    let opts = [...options[type as string]]
    if (
      decodedSearchKeyword &&
      (type === 'contents' || type === 'category_contents' || type === 'solutions') &&
      !hideKeywordSort
    ) {
      opts = [{ id: 'bykeyword', label: 'Keyword' }, ...opts]
    }
    if (
      (fromPage === 'graveyard-consolidations' ||
        fromPage === 'my-following' ||
        fromPage === 'my-private-portfolio' ||
        fromPage === 'my-shared-portfolio' ||
        fromPage === 'custom-tag') &&
      type === 'solutions'
    ) {
      opts = opts.filter((option) => option.id !== 'Newest')
    }
    return opts
  }, [type, decodedSearchKeyword, fromPage, hideKeywordSort])

  const [selected, setSelected] = useState(sortByOptions[0])
  const { updateQueryParams } = useUpdateQueryParams()
  const prevKeywordRef = useRef<string | undefined>()
  const isInitialMountRef = useRef(true)
  const hasRouterBeenReadyRef = useRef(false)

  // When search keyword changes (user performs new search), default sort to Keyword for contents.
  // Skip on initial mount and when router first becomes ready (refresh/hydration) to preserve sort from URL.
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevKeywordRef.current = decodedSearchKeyword
      if (router.isReady) hasRouterBeenReadyRef.current = true
      return
    }

    // First time router becomes ready = hydration from URL (e.g. refresh). Don't overwrite sort.
    if (router.isReady && !hasRouterBeenReadyRef.current) {
      hasRouterBeenReadyRef.current = true
      prevKeywordRef.current = decodedSearchKeyword
      return
    }

    const keywordChanged = prevKeywordRef.current !== decodedSearchKeyword

    if (keywordChanged && (type === 'contents' || type === 'category_contents' || type === 'solutions')) {
      const defaultSortForType =
        type === 'solutions' ? 'Default' : type === 'contents' ? 'Chronologically' : 'Category_Chronologically'
      let defaultSort: string | undefined = undefined
      const decodedSort = typeof router.query.sort === 'string' ? decodeURIComponent(router.query.sort) : ''

      // User cleared keyword: reset to default sort (Keyword option is removed when no keyword)
      if (decodedSearchKeyword === undefined) {
        if (type === 'solutions') {
          if (decodedSort === 'bykeyword') {
            defaultSort = 'Default'
            updateQueryParams({ sort: defaultSort, page: undefined }, false)
          }
        } else {
          if (decodedSort === 'bykeyword') {
            defaultSort = type === 'contents' ? 'Chronologically' : 'Category_Chronologically'
            updateQueryParams({ sort: defaultSort, page: undefined }, false)
          }
        }
      } else if (!hideKeywordSort) {
        // User added/changed keyword: default to Keyword sort
        defaultSort = decodedSort !== 'bykeyword' ? 'bykeyword' : sortByOptions[0]?.id ?? defaultSortForType
        updateQueryParams({ sort: defaultSort, page: undefined }, false)
      } else {
        // hideKeywordSort: use default when adding keyword
        defaultSort = sortByOptions[0]?.id ?? defaultSortForType
        updateQueryParams({ sort: defaultSort, page: undefined }, false)
      }
    }
    // if (isInitialMount.current) {
    //   isInitialMount.current = false
    // }
    // const decodedSortOption = typeof router.query.sort === 'string' ? decodeURIComponent(router.query.sort) : ''
    // updateQueryParams({ sort: decodedSortOption, page: undefined }, false)
    prevKeywordRef.current = decodedSearchKeyword
  }, [decodedSearchKeyword, type, updateQueryParams, sortByOptions, hideKeywordSort, router.isReady])

  // Sync selected from URL - wait for router.isReady so we read the correct sort on refresh
  useEffect(() => {
    if (!router.isReady) return
    if (typeof router.query.sort === 'string') {
      const selectedOption = sortByOptions.find(({ id }) => id === router.query.sort)
      setSelected(selectedOption || sortByOptions[0])
    } else {
      setSelected(sortByOptions[0])
    }
  }, [router.isReady, router.query.sort, sortByOptions])

  const onChange = (value: SortbyOption) => {
    setSelected(value)
    updateQueryParams({
      sort: value.id,
      page: undefined,
    })
  }

  const fromPageClass = ['EnhancedResourcePagination', 'SolutionVendorAnalysisContainer']
  const mtClass = fromPageClass.includes(fromPage) ? 'md:mt-0' : 'md:mt-1'

  return (
    <div className={cn(className, 'top-16')}>
      <Listbox value={selected} onChange={onChange}>
        <div className={cn('relative ', mtClass)}>
          <Listbox.Label></Listbox.Label>
          <Listbox.Button className="relative w-full py-2 pl-3 pr-10 border-mist border-solid border !rounded-[20px] text-left min-h-[42px] bg-white">
            {({ open }) => (
              <>
                <span className="block truncate">
                  <b>Sort by: </b>
                  {selected && selected.label}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  {open ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  )}
                </span>
              </>
            )}
          </Listbox.Button>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-textdark bg-white rounded-[20px] shadow-lg">
              {sortByOptions.map((option, index) => (
                <Listbox.Option
                  key={index}
                  className={({ active }) =>
                    `${active ? 'bg-turquoise bg-opacity-10 text-textlight' : 'text-gray-900'}
                          cursor-default select-none relative py-2 pl-10 pr-4`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <>
                      <span className={`${selected ? 'font-medium' : 'font-normal'} block truncate`}>
                        {option.label}
                      </span>
                      {selected ? (
                        <span
                          className={`${active ? 'text-amber-600' : 'text-amber-600'}
                                absolute inset-y-0 left-0 flex items-center pl-3`}
                        >
                          <CheckIcon className="w-5 h-5 text-black" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}
